import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ConfigurationLoader} from '../../core/configuration/services/configuration.loader';
import {ConfigurationService} from '../../core/configuration/services/configuration.service';
import {UserData} from '../../core/user/models/user-data';
import {UserService} from '../../core/user/services/user.service';

/**
 * Roles granting access to the Customer Data Frontend. They are kept in sync with the roles configured for the
 * customer data app in the environment files.
 */
const CUSTOMER_DATA_FRONTEND_ROLES: string[] = [
  'cdf_user',
  'cdf_user_read',
  'cdf_admin_global',
  'cdf_admin_local_write',
  'cdf_admin_local_read'
];

const WEB_COMPONENT_SCRIPT_PATH: string = '/main.js';

/**
 * Provides the configuration needed to embed the Customer Data Frontend's web components.
 */
@Injectable({providedIn: 'root'})
export class CustomerDataFrontendService {
  private readonly configurationLoader: ConfigurationLoader = inject(ConfigurationLoader);
  private readonly userService: UserService = inject(UserService);

  /**
   * Emits whenever the configuration which determines the availability of the Customer Data Frontend has changed.
   */
  readonly availabilityChanged: Observable<unknown> = this.configurationLoader.configChanged;

  /**
   * True if the current user may use the Customer Data Frontend, i.e. the user has one of the Customer Data Frontend
   * roles, the market has enabled the Customer Data Frontend and a Customer Data Frontend URL is configured for the
   * current portal host.
   */
  isEnabled(): boolean {
    return this.userHasCustomerDataFrontendRole()
      && !!this.configurationLoader.getLeadsDealerConfig()?.customerDataFrontend
      && !!this.getBaseUrl();
  }

  /**
   * The base URL of the Customer Data Frontend, used by its web component to load its own resources.
   */
  getBaseUrl(): string {
    return ConfigurationService.getUrlForPortalHost('customerData');
  }

  /**
   * The URL of the script which registers the Customer Data Frontend's web components.
   */
  getWebComponentScriptUrl(): string {
    const baseUrl: string = this.getBaseUrl();
    return baseUrl ? baseUrl + WEB_COMPONENT_SCRIPT_PATH : undefined;
  }

  private userHasCustomerDataFrontendRole(): boolean {
    const user: UserData = this.userService.getCurrentUser();
    return !!user && CUSTOMER_DATA_FRONTEND_ROLES.some((role: string): boolean => user.getRoles().includes(role));
  }
}
